import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { devNoteService } from "../../services/devNoteService";
import { useToast } from "../../context/ToastContext";
import Icon from "../../components/Common/Icon";
import DevNoteModal from "../../components/Dev/DevNoteModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";

const COLUMNS_META = {
  IN_PROGRESS: {
    title: "Geliştiriliyor (In Progress)",
    icon: "autorenew",
    iconCls: "text-primary bg-primary/10",
    badgeCls: "bg-primary/10 text-primary border-primary/20",
  },
  PLANNED: {
    title: "Planlananlar (Planned)",
    icon: "event",
    iconCls: "text-tier-gold bg-tier-gold/10",
    badgeCls: "bg-tier-gold/10 text-tier-gold border-tier-gold/20",
  },
  BACKLOG: {
    title: "Backlog & Fikirler (Backlog)",
    icon: "lightbulb",
    iconCls: "text-success bg-success/10",
    badgeCls: "bg-success/10 text-success border-success/20",
  },
};

export default function DevRoadmapPage() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === "ADMIN";
  const { showToast } = useToast();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await devNoteService.listByType("ROADMAP");
      setNotes(res.data.data ?? []);
    } catch (err) {
      showToast("Yol haritası yüklenemedi.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateClick = () => {
    setSelectedNote(null);
    setModalOpen(true);
  };

  const handleEditClick = (note) => {
    setSelectedNote(note);
    setModalOpen(true);
  };

  const handleDeleteClick = (note) => {
    setConfirmModal({
      isOpen: true,
      title: "Yol Haritası Öğesini Sil",
      message: `"${note.title}" başlıklı yol haritası öğesini silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await devNoteService.delete(note.id);
          showToast("Yol haritası öğesi başarıyla silindi.", "success");
          fetchNotes();
        } catch (err) {
          showToast("Öğe silinemedi.", "error");
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleModalSubmit = async (data) => {
    try {
      if (selectedNote) {
        await devNoteService.update(selectedNote.id, data);
        showToast("Öğe başarıyla güncellendi.", "success");
      } else {
        await devNoteService.create(data);
        showToast("Yol haritası öğesi başarıyla oluşturuldu.", "success");
      }
      setModalOpen(false);
      fetchNotes();
    } catch (err) {
      showToast("İşlem gerçekleştirilemedi.", "error");
    }
  };

  // Group columns
  const getColumnItems = (status) => notes.filter((n) => n.status === status);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="track_changes" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Gelecek Özellikler (Yol Haritası)</h1>
            <p className="text-on-surface-variant text-sm">
              Geliştirme aşamasında olan yenilikler ve salon yönetim sistemine eklenmesi planlanan hedefler.
            </p>
          </div>
        </div>

        {isAdmin && (
          <button
            onClick={handleCreateClick}
            className="bg-primary text-white px-4 py-2 rounded-xl text-xs font-semibold hover:opacity-90 transition flex items-center gap-1.5 self-start sm:self-auto shadow-sm"
          >
            <Icon name="add" className="!text-[16px]" /> Yeni Ekle
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          <p>Yol haritası yükleniyor…</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries(COLUMNS_META).map(([status, meta]) => {
            const columnItems = getColumnItems(status);
            return (
              <div
                key={status}
                className="bg-surface-container-low rounded-3xl p-5 border border-outline-variant/40 shadow-sm flex flex-col gap-4 animate-slide-in"
              >
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.iconCls}`}>
                    <Icon name={meta.icon} className="!text-[18px]" />
                  </span>
                  <h3 className="font-bold text-sm text-on-surface">{meta.title}</h3>
                  <span className="text-xs text-on-surface-variant bg-surface-container px-2 py-0.5 rounded-full font-semibold">
                    {columnItems.length}
                  </span>
                </div>

                <div className="flex-1 space-y-3">
                  {columnItems.length === 0 ? (
                    <div className="text-center py-6 text-xs text-on-surface-variant border border-dashed border-outline-variant rounded-2xl bg-white/50">
                      Öğe bulunmuyor.
                    </div>
                  ) : (
                    columnItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white p-4 rounded-2xl border border-outline-variant/30 shadow-sm space-y-2 flex flex-col justify-between hover:border-primary/20 transition-all group"
                      >
                        <div className="space-y-1">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className="font-bold text-xs text-on-surface leading-tight">
                              {item.title}
                            </h4>
                            {item.badgeText && (
                              <span
                                className={`px-2 py-0.5 rounded-full text-[9px] font-bold border flex-shrink-0 ${meta.badgeCls}`}
                              >
                                {item.badgeText}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-on-surface-variant leading-relaxed">
                            {item.description}
                          </p>
                        </div>

                        {isAdmin && (
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity border-t border-outline-variant/30 pt-2 mt-1">
                            <button
                              onClick={() => handleEditClick(item)}
                              className="p-1 rounded text-primary hover:bg-primary/10 transition"
                              title="Düzenle"
                            >
                              <Icon name="edit" className="!text-[14px]" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(item)}
                              className="p-1 rounded text-error hover:bg-error/10 transition"
                              title="Sil"
                            >
                              <Icon name="delete" className="!text-[14px]" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DevNote Add/Edit Modal */}
      <DevNoteModal
        isOpen={modalOpen}
        initialData={selectedNote}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type="danger"
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
